import type { ScriptTarget } from "@kensio/pinyinjs";

import { wireCopyButton } from "./copy-button.js";
import { plainFlavours } from "./copy-html.js";
import { slot } from "./dom.js";

/**
 * The converter's output panels: one orthography each, all of them at once.
 *
 * The tool used to ask which script was wanted and show one answer. It does
 * not any more, for the reason the pinyin page shows bopomofo and Wade-Giles
 * beside the pinyin rather than behind a control: the hard part of the work —
 * reading the text to decide which form each character takes — is the same for
 * every target, so showing one and hiding the rest saves nothing and costs a
 * decision the visitor has to make before they can read anything.
 *
 * A panel names its orthography with `data-cb-script`, so which ones a page
 * offers and in what order is settled by the template. A panel naming
 * something this does not know is skipped rather than filled with the wrong
 * script.
 */

/**
 * The orthographies a panel may ask for.
 *
 * `zh-Hant` is deliberately not among them. It means Taiwan's forms, because
 * there is no region-free 繁體 to fall back on, and a panel headed
 * "Traditional" that quietly meant Taiwan would be hiding the difference
 * rather than showing it.
 */
const panelTargets = [
  "zh-Hans",
  "zh-Hant-TW",
  "zh-Hant-HK",
] as const satisfies readonly ScriptTarget[];

/** One of the orthographies a panel is allowed to name. */
export type PanelTarget = (typeof panelTargets)[number];

/**
 * How each orthography is named in a sentence.
 *
 * Shorter than the headings, which have to work as headings and carry their
 * BCP 47 tag with them. These are for the line under a converted character
 * saying which of the panels above it came out of.
 */
const panelNames: Readonly<Record<PanelTarget, string>> = {
  "zh-Hans": "简体",
  "zh-Hant-TW": "繁體 (Taiwan)",
  "zh-Hant-HK": "繁體 (Hong Kong)",
};

/** What an orthography is called on the page. */
export function panelName(target: PanelTarget): string {
  return panelNames[target];
}

/** One output panel: what it writes, where it writes it, and its copy button. */
export interface ScriptPanel {
  readonly target: PanelTarget;
  readonly output: HTMLElement;
  readonly copy: HTMLButtonElement | undefined;
}

/**
 * The orthography a panel named, if it named one this converter writes.
 */
export function panelTarget(
  named: string | undefined,
): PanelTarget | undefined {
  return panelTargets.find((target) => target === named);
}

/**
 * Every panel under a container that names an orthography and has room for it.
 */
export function findScriptPanels(
  container: HTMLElement,
): readonly ScriptPanel[] {
  const found: ScriptPanel[] = [];

  for (const panel of container.querySelectorAll<HTMLElement>(
    "[data-cb-script]",
  )) {
    const target = panelTarget(panel.dataset["cbScript"]);
    const output = slot(panel, "output");

    if (target === undefined || output === undefined) {
      continue;
    }

    const copy = slot(panel, "copy");

    found.push({
      target,
      output,
      copy: copy instanceof HTMLButtonElement ? copy : undefined,
    });
  }

  return found;
}

/**
 * Give each panel's copy button the text that panel is showing.
 *
 * Read off the panel rather than kept alongside it, so a button can only ever
 * copy what is on the screen above it. Plain text: the marks on the guesses
 * are an annotation about this page and mean nothing in a document.
 */
export function wireScriptCopies(panels: readonly ScriptPanel[]): void {
  for (const { copy, output } of panels) {
    if (copy === undefined) {
      continue;
    }

    wireCopyButton(copy, () => {
      const written = output.textContent;

      return written === "" ? undefined : plainFlavours(written);
    });
  }
}
